import React, { useLayoutEffect } from 'react';
import { Control } from 'react-hook-form';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Button } from '@/components/ui/therapist/Button';
import { Text } from '@/components/ui/therapist/Text';
import colors from '@/config/colors';
import { LoginLayout, MAX_WIDTH } from './Layout';
import { LoginFormInputs } from './LoginFormInputs';
import { StayLoggedIn } from './StayLoggedIn';
import { styles } from './styles';

interface LoginFormData {
  email: string;
  password: string;
  twoFAToken?: string;
  app?: string;
  deviceModel?: string;
  shortLivedSession?: boolean;
  deviceToken?: string;
  loginChannel?: string;
}

interface TherapistLoginProps {
  lastUsedEmail?: string;
  control: Control<LoginFormData>;
  stayLoggedIn: boolean;
  setStayLoggedIn: (value: boolean) => void;
  onPasswordForgotten: () => void;
  onRegister: () => void;
  onLogin: () => void;
  onDemo: () => void;
}

export const TherapistLogin: React.FC<TherapistLoginProps> = ({
  lastUsedEmail,
  control,
  stayLoggedIn,
  setStayLoggedIn,
  onPasswordForgotten,
  onRegister,
  onLogin,
  onDemo,
}) => {
  const navigation = useNavigation();

  useLayoutEffect(() => {
    if (!lastUsedEmail) {
      navigation.setOptions({ title: '' });
    }
  }, []);

  const LoginForm = () => {
    return (
      <>
        <Text style={{ marginBottom: 16 }}>
          Melden Sie sich mit Ihren Zugangsdaten an.
        </Text>
        <LoginFormInputs control={control} />
        <View style={{ alignItems: 'flex-end' }}>
          <Button
            label="Passwort vergessen?"
            onPress={onPasswordForgotten}
            look="tertiary"
          />
        </View>
        <StayLoggedIn checked={stayLoggedIn} onToggle={setStayLoggedIn} />
      </>
    );
  };

  const renderReturningUserContent = () => {
    return (
      <LoginLayout
        scrollableHeader={
          <Text style={styles.therapistWelcomeText}>👋 Willkommen zurück!</Text>
        }
        outerButtons={
          <View style={styles.therapistMultipleButtonsContainer}>
            <Button label="Registrieren" look="tertiary" onPress={onRegister} />
            <Button label="Anmelden" look="primary" onPress={onLogin} />
          </View>
        }
      >
        <LoginForm />
      </LoginLayout>
    );
  };

  const renderNewUserContent = () => {
    return (
      <LoginLayout
        scrollableHeader={
          <View style={{ alignItems: 'center', justifyContent: 'center' }}>
            <View style={{ width: MAX_WIDTH }}>
              <Text style={styles.therapistWelcomeText}>
                Willkommen in der Aphasie-App für Therapeut:innen!
              </Text>
              <View
                style={{
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  marginBottom: 24,
                }}
              >
                <Button
                  label="Demo ansehen "
                  look="secondary"
                  onPress={onDemo}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    marginRight: 8,
                  }}
                />
                <Button
                  label="Neu hier? Jetzt kostenlos registrieren "
                  look="primary"
                  onPress={onRegister}
                  style={{ flex: 1, justifyContent: 'center' }}
                />
              </View>
            </View>
          </View>
        }
      >
        <LoginForm />
        <View style={styles.therapistSingleButtonContainer}>
          <Button label="Anmelden" look="primary" onPress={onLogin} />
        </View>
      </LoginLayout>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: colors.newUI.therapist.blue20,
      }}
    >
      {lastUsedEmail ? renderReturningUserContent() : renderNewUserContent()}
    </View>
  );
};
